
import { btnStyle, tdStyle, thStyle } from './styles';
import OrganicEmptyState from '../../components/organic/OrganicEmptyState.jsx';
import { resolvePhotoUrl } from '../../utils/photoUrl';

/**
 * The list of collections.
 *
 * A row that exists only in this device's sync queue cannot be opened,
 * because there is nothing on the server to open yet -- it is shown with a
 * badge instead, so the work looks saved (it is) rather than missing.
 * A record Field Mode or the map still holds on this device (`_deviceOnly`)
 * is shown and not opened for the same reason, with a badge of its own, which
 * says when Field Mode is waiting for the person.
 *
 * Each row says what the map says of it, from the same summary
 * (GET /collections): its code -- a specimen's, else its own QR -- whether it
 * is an observation or a collection, in the map's words (`map:kind.*`), and
 * its photographs. `t` must have the `map` namespace loaded.
 *
 * With no collections at all it says so with the empty-state art, and offers
 * `onCreate` (the New Collection button's own handler) when it is given --
 * the page passes null while the wizard is open, and the empty state then
 * stays out of the way altogether.
 */
export default function CollectionView({
  t, rows, queuedIds, loading, error, expanded, setExpanded, onOpen, onResumeDraft,
  onCreate = null, user, canEdit, isCollector,
}) {
  const headers = [t('table.photos'), t('table.code'), t('table.kind'), t('table.date'), t('table.region'),
    t('table.substrate'), t('table.rank'), t('table.species'), t('table.labStatus'), t('table.status')];
  return (
    <>
  {/* A failed server read does not hide what IS known -- the records on
      this device and in the queue, which matter most offline. The page
      shows the error above. */}
  {!loading && rows.length > 0 && (
    <button type="button" onClick={() => setExpanded(e => !e)} title={t('tip.toggleRecords')}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary-fg)', fontSize: 14, fontWeight: 600, padding: '4px 0', marginBottom: 8 }}>
      {expanded ? t('table.hideRecords', { count: rows.length }) : t('table.showRecords', { count: rows.length })}
    </button>
  )}

  {!loading && !error && rows.length === 0 && onCreate && (
    <OrganicEmptyState kind="collections" title={t('noCollectionsYet')} hint={t('empty.hint')}
      action={(
        <button type="button" onClick={onCreate} style={btnStyle('var(--c-primary)')}
          data-testid="collections-empty-create" title={t('tip.newCollection')}>
          {t('empty.action')}
        </button>
      )} />
  )}

  {!loading && expanded && rows.length > 0 && (
      <div className="table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr style={{ background: 'var(--c-bg-muted-2)' }}>
              {headers.map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(c => {
              const openable = !c._queuedOnly && !c._deviceOnly;
              return (
              <tr key={c.sample_id} data-testid={`collection-row-${c.sample_id}`}
                onClick={() => { if (openable) onOpen(c.sample_id); }}
                title={openable ? t('tip.openRow') : undefined}
                style={{ borderBottom: '1px solid var(--c-bg-muted-3)', cursor: openable ? 'pointer' : 'default' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--c-bg-subtle)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={tdStyle} data-testid={`collection-photos-${c.sample_id}`}>
                  {c.thumbnail_url || c.photo_count > 0 ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {/* Lazily: a long table is not a page of photographs
                          fetched before it can be read. */}
                      {c.thumbnail_url && (
                        <img src={resolvePhotoUrl(c.thumbnail_url)} alt="" width={40} height={40}
                          loading="lazy" decoding="async" style={thumbStyle} />
                      )}
                      {c.photo_count > 0 && <span>{c.photo_count}</span>}
                    </span>
                  ) : '—'}
                </td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }} dir="ltr">{c.code || c.qr_uid || '—'}</td>
                <td style={tdStyle}>
                  {c.kind ? (
                    <span data-testid={`kind-${c.sample_id}`} data-kind={c.kind}
                      style={{ ...pillStyle, ...(KIND_PILL[c.kind] || KIND_PILL.observation) }}>
                      {t(`map:kind.${c.kind}`)}
                    </span>
                  ) : '—'}
                </td>
                <td style={tdStyle}>{c.collection_datetime ? (c.collection_datetime.length === 4 ? c.collection_datetime : new Date(c.collection_datetime).toLocaleDateString()) : '—'}</td>
                <td style={tdStyle}>{c.region ? t(c.region, { ns: 'regions' }) : '—'}</td>
                <td style={tdStyle}>{c.substrate ? t(`substrate.${c.substrate}`) : '—'}</td>
                <td style={tdStyle}>{c.current_rank ? t(`common:rank.${c.current_rank}`) : '—'}</td>
                <td style={{ ...tdStyle, fontStyle: 'italic' }} dir="ltr">{c.current_taxon_name || '—'}</td>
                <td style={tdStyle}>{c.lab_status ? t(`labStatus.${c.lab_status}`) : '—'}</td>
                <td style={tdStyle}>
                  {c._deviceOnly && (
                    <span data-testid={`device-badge-${c.sample_id}`} data-attention={c.attention ? 'true' : 'false'}
                      title={t('table.deviceBadgeTitle')}
                      style={{ ...pillStyle, marginInlineEnd: 6, ...(c.attention ? ATTENTION_PILL : DEVICE_PILL) }}>
                      {c.attention ? t('table.deviceAttention') : t('table.deviceBadge')}
                    </span>
                  )}
                  {queuedIds.has(c.sample_id) && (
                    <span data-testid={`queued-badge-${c.sample_id}`}
                      title={t('table.queuedBadgeTitle')}
                      style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                        fontSize: 11, fontWeight: 600, marginInlineEnd: 6,
                        background: 'var(--c-info-soft)',
                        color: 'var(--c-info-fg)',
                      }}>
                      {t('table.queuedBadge')}
                    </span>
                  )}
                  {c.is_draft == null ? (c._deviceOnly ? null : '—') : (
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: c.is_draft ? 'var(--c-warning-soft)' : 'var(--c-success-soft)',
                      color:      c.is_draft ? 'var(--c-warning-text-fg)' : 'var(--c-success-fg)',
                    }}>
                      {c.is_draft ? t('table.draftBadge') : t('table.submittedBadge')}
                    </span>
                  )}
                  {openable && c.is_draft && ((c.collector_id === user?.user_id && isCollector) || canEdit) && (
                    <button type="button"
                      onClick={e => { e.stopPropagation(); onResumeDraft(c.sample_id); }}
                      title={t('tip.continueDraft')}
                      style={{ ...btnStyle('var(--c-primary)'), marginInlineStart: 8, padding: '2px 10px', fontSize: 11 }}>
                      {t('table.continueBtn')}
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
  )}
    </>
  );
}

const thumbStyle = { width: 40, height: 40, objectFit: 'cover', borderRadius: 4, display: 'block' };
const pillStyle = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 };
// The map's two kinds, in theme colours: its pins are blue and green.
const KIND_PILL = {
  observation: { background: 'var(--c-info-soft)', color: 'var(--c-info-fg)' },
  collection: { background: 'var(--c-success-soft)', color: 'var(--c-success-fg)' },
};
// As Field Mode's gallery shows a batch on its way, and one needing the person.
const DEVICE_PILL = { background: 'var(--c-bg-subtle-6)', color: 'var(--c-text-muted-3-fg)' };
const ATTENTION_PILL = { background: 'var(--c-danger-soft-2)', color: 'var(--c-danger-4-fg)' };
